import { formatDollars } from '@fruitful/fruitils';
import { buildDraftedMessageAgentPayload } from '@fruitful/llm-service-shared';
import {
    type EstimatedBill,
    type EstimatedBillsWidgetProps,
} from '@fruitful/types';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { type StyleProp, TextInput, type TextStyle } from 'react-native';
import {
    BodyLargeRegular,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    getDefaultFontFamily,
    H6Regular,
    Icon,
    isNonMobileWeb,
    Shadows,
    View,
    XIcon,
    XStack,
    YStack,
} from 'smoothie';

import storage from '../../../common/providers/StorageFacade';

const BILLS_TOOLTIP_SEEN_KEY = 'CMM_BILLS_WIDGET_TOOLTIP_SEEN';
const TOOLTIP_AUTO_DISMISS_MS = 3000;

const EditHintTooltip = ({ children }: { children: React.ReactNode }) => {
    const [visible, setVisible] = useState(() => {
        return storage.getBool(BILLS_TOOLTIP_SEEN_KEY) !== true;
    });

    useEffect(() => {
        if (!visible) return undefined;
        const timer = setTimeout(() => {
            setVisible(false);
            storage.setBool(BILLS_TOOLTIP_SEEN_KEY, true);
        }, TOOLTIP_AUTO_DISMISS_MS);
        return () => {
            clearTimeout(timer);
        };
    }, [visible]);

    const dismiss = useCallback(() => {
        setVisible(false);
        storage.setBool(BILLS_TOOLTIP_SEEN_KEY, true);
    }, []);

    return (
        <YStack width="100%" alignItems="center">
            {visible && (
                <YStack alignItems="center" marginBottom={8} onPress={dismiss}>
                    <XStack
                        backgroundColor="$accent1000"
                        opacity={0.9}
                        borderRadius={8}
                        padding={12}
                        gap={4}
                        alignItems="center"
                        cursor="pointer"
                        onPress={dismiss}
                    >
                        <BodySmallRegular color="$white">
                            Tap any row to edit
                        </BodySmallRegular>
                        <Icon icon={XIcon} size={12} color="$white" />
                    </XStack>
                    <View
                        width={8}
                        height={8}
                        backgroundColor="$accent1000"
                        opacity={0.9}
                        marginTop={-4}
                        style={{ transform: [{ rotate: '45deg' }] }}
                    />
                </YStack>
            )}
            {children}
        </YStack>
    );
};

interface EstimatedBillsWidgetComponentProps extends EstimatedBillsWidgetProps {
    disabled?: boolean;
    isActive?: boolean;
    onConfirm: (
        displayText: string,
        agentPayload: string,
    ) => void | Promise<void>;
    onEditingChange?: (isEditing: boolean) => void;
}

// --- Desktop inline edit cell ---

const formatWithCommas = (digits: string): string => {
    if (digits === '') return '';
    return Number(digits).toLocaleString('en-US');
};

interface EditableAmountCellProps {
    bill: EstimatedBill;
    editedCents: number;
    isEditing: boolean;
    isDisabled: boolean;
    onStartEdit: () => void;
    onCommit: (newCents: number) => void;
}

const EditableAmountCell = ({
    bill,
    editedCents,
    isEditing,
    isDisabled,
    onStartEdit,
    onCommit,
}: EditableAmountCellProps) => {
    const [draft, setDraft] = useState('');
    const inputRef = useRef<TextInput>(null);

    useEffect(() => {
        if (isEditing) {
            setDraft('');
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [isEditing]);

    const handleStartEdit = useCallback(() => {
        if (isDisabled) return;
        onStartEdit();
    }, [isDisabled, onStartEdit]);

    const handleCommit = useCallback(() => {
        if (draft === '') {
            onCommit(editedCents);
            return;
        }
        const dollars = parseInt(draft, 10);
        onCommit(Number.isNaN(dollars) ? editedCents : dollars * 100);
    }, [draft, editedCents, onCommit]);

    const placeholderValue = formatWithCommas(
        Math.round(editedCents / 100).toString(),
    );

    if (isDisabled) {
        return (
            <BodyMediumRegular>
                {formatDollars(editedCents / 100, false)}
            </BodyMediumRegular>
        );
    }

    if (isEditing) {
        const formattedDraft = formatWithCommas(draft);
        const displayValue = formattedDraft;
        const displayText = displayValue || placeholderValue;
        const digitCount = displayText.replace(/[^0-9]/g, '').length;
        const commaCount = displayText.replace(/[^,]/g, '').length;
        const charWidth = digitCount + commaCount * 0.4;

        const inputStyle: StyleProp<TextStyle> = {
            fontFamily: getDefaultFontFamily(),
            fontSize: 16,
            fontWeight: '400',
            lineHeight: 22,
            paddingVertical: 0,
            paddingHorizontal: 0,
            width: `${Math.max(charWidth, 1)}ch` as unknown as number,
        };

        return (
            <XStack alignItems="center" gap={0}>
                <BodyMediumRegular>$</BodyMediumRegular>
                <TextInput
                    ref={inputRef}
                    value={displayValue}
                    placeholder={placeholderValue}
                    placeholderTextColor="#999"
                    onChangeText={(text) => {
                        setDraft(text.replace(/[^0-9]/g, ''));
                    }}
                    onBlur={handleCommit}
                    onSubmitEditing={handleCommit}
                    keyboardType="number-pad"
                    style={inputStyle}
                    aria-label={`Edit ${bill.label} amount`}
                />
            </XStack>
        );
    }

    return (
        <BodyMediumRegular
            cursor="pointer"
            onPress={handleStartEdit}
            textDecorationLine="underline"
            textDecorationStyle="dashed"
            textDecorationColor="$gray200"
        >
            {formatDollars(editedCents / 100, false)}
        </BodyMediumRegular>
    );
};

// --- Mobile full-view editor ---

interface MobileEditViewProps {
    bill: EstimatedBill;
    currentCents: number;
    onSave: (newCents: number) => void;
    onBack: () => void;
}

const MobileEditView = ({
    bill,
    currentCents,
    onSave,
    onBack,
}: MobileEditViewProps) => {
    const [draft, setDraft] = useState('');
    const inputRef = useRef<TextInput>(null);

    const placeholderValue = formatWithCommas(
        currentCents > 0 ? Math.round(currentCents / 100).toString() : '0',
    );

    useEffect(() => {
        setTimeout(() => inputRef.current?.focus(), 100);
    }, []);

    const handleClear = useCallback(() => {
        setDraft('');
        inputRef.current?.focus();
    }, []);

    const handleSave = useCallback(() => {
        if (draft === '') {
            onSave(currentCents);
            return;
        }
        const dollars = parseInt(draft, 10);
        onSave(Number.isNaN(dollars) ? 0 : dollars * 100);
    }, [draft, currentCents, onSave]);

    return (
        <YStack width="100%" gap={20}>
            <H6Regular>{bill.label}</H6Regular>

            <XStack
                width="100%"
                backgroundColor="$gray100"
                borderRadius={12}
                paddingHorizontal={16}
                paddingVertical={14}
                alignItems="center"
                justifyContent="space-between"
            >
                <XStack alignItems="center" flex={1}>
                    <BodyMediumRegular color="$gray1100">$ </BodyMediumRegular>
                    <TextInput
                        ref={inputRef}
                        value={formatWithCommas(draft)}
                        onChangeText={(text) => {
                            setDraft(text.replace(/[^0-9]/g, ''));
                        }}
                        onSubmitEditing={handleSave}
                        keyboardType="number-pad"
                        placeholder={placeholderValue}
                        placeholderTextColor="#999"
                        style={
                            {
                                fontFamily: getDefaultFontFamily(),
                                fontSize: 16,
                                fontWeight: '400',
                                lineHeight: 22,
                                flex: 1,
                                paddingVertical: 0,
                            } as const
                        }
                        aria-label={`Amount for ${bill.label}`}
                    />
                </XStack>
                {draft.length > 0 && (
                    <BodyMediumRegular
                        color="$black"
                        onPress={handleClear}
                        cursor="pointer"
                    >
                        Clear
                    </BodyMediumRegular>
                )}
            </XStack>

            <XStack width="100%" gap={12}>
                <YStack flex={1}>
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.LARGE}
                        text="Back"
                        width="full"
                        onPress={onBack}
                    />
                </YStack>
                <YStack flex={1}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Save"
                        width="full"
                        onPress={handleSave}
                    />
                </YStack>
            </XStack>
        </YStack>
    );
};

// --- Main widget ---

export const EstimatedBillsWidget = ({
    bills,
    confirmationPrefix,
    confirmationPayload,
    disabled = false,
    isActive = true,
    onConfirm,
    onEditingChange,
}: EstimatedBillsWidgetComponentProps) => {
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const isWidgetDisabled = disabled || !isActive || hasSubmitted;

    const [editedAmounts, setEditedAmounts] = useState<Record<string, number>>(
        () => {
            const entries: Array<[string, number]> = bills.map((bill) => [
                bill.category,
                bill.amountCents,
            ]);
            return Object.fromEntries(entries);
        },
    );
    const [editingCategory, setEditingCategory] = useState<string | null>(null);

    useEffect(() => {
        if (!isNonMobileWeb) {
            onEditingChange?.(editingCategory != null);
        }
    }, [editingCategory, onEditingChange]);

    const prevBillsRef = useRef(bills);
    useEffect(() => {
        const prevByCategory = new Map(
            prevBillsRef.current.map((b) => [b.category, b.amountCents]),
        );
        setEditedAmounts((prev) => {
            const next = { ...prev };
            for (const bill of bills) {
                const prevServer = prevByCategory.get(bill.category);
                if (prevServer == null || prevServer !== bill.amountCents) {
                    next[bill.category] = bill.amountCents;
                }
            }
            const activeCategories = new Set(bills.map((b) => b.category));
            return Object.fromEntries(
                Object.entries(next).filter(([key]) =>
                    activeCategories.has(key),
                ),
            );
        });
        prevBillsRef.current = bills;
    }, [bills]);

    const effectiveBills: EstimatedBill[] = useMemo(
        () =>
            bills.map((bill) => ({
                ...bill,
                amountCents: editedAmounts[bill.category] ?? bill.amountCents,
            })),
        [bills, editedAmounts],
    );

    const totalCents = useMemo(
        () => effectiveBills.reduce((sum, bill) => sum + bill.amountCents, 0),
        [effectiveBills],
    );

    const handleCommitAmount = useCallback(
        (category: string, newCents: number) => {
            setEditedAmounts((prev) => ({ ...prev, [category]: newCents }));
            setEditingCategory(null);
        },
        [],
    );

    const handleConfirm = useCallback(() => {
        if (isWidgetDisabled) {
            return;
        }

        const updatedPayload = {
            ...confirmationPayload,
            bills: effectiveBills,
        };

        // Show the estimated total as the member's message (e.g.
        // "Estimated bills: $2,715") instead of "Looks good". The
        // post-confirmation bills recap is dropped as well, matching the
        // debt-amount approval UX.
        const confirmationLabel = `Estimated bills: ${formatDollars(
            totalCents / 100,
            false,
        )}`;

        const agentPayload = buildDraftedMessageAgentPayload({
            label: confirmationLabel,
            confirmationPrefix,
            confirmationPayload: updatedPayload,
        });

        setHasSubmitted(true);
        void onConfirm(confirmationLabel, agentPayload);
    }, [
        confirmationPayload,
        confirmationPrefix,
        effectiveBills,
        isWidgetDisabled,
        onConfirm,
        totalCents,
    ]);

    const mobileEditingBill = useMemo(
        () =>
            !isNonMobileWeb && editingCategory != null
                ? effectiveBills.find((b) => b.category === editingCategory)
                : null,
        [editingCategory, effectiveBills],
    );

    if (!isNonMobileWeb && mobileEditingBill != null) {
        return (
            <YStack
                width="100%"
                padding={20}
                backgroundColor="$white"
                borderRadius={20}
                gap={16}
                {...Shadows.SMALL}
            >
                <MobileEditView
                    bill={mobileEditingBill}
                    currentCents={mobileEditingBill.amountCents}
                    onSave={(newCents) => {
                        handleCommitAmount(
                            mobileEditingBill.category,
                            newCents,
                        );
                    }}
                    onBack={() => {
                        setEditingCategory(null);
                    }}
                />
            </YStack>
        );
    }

    return (
        <EditHintTooltip>
            <YStack
                width="100%"
                padding={20}
                backgroundColor="$white"
                borderRadius={20}
                gap={8}
                {...Shadows.SMALL}
            >
                <XStack
                    width="100%"
                    justifyContent="space-between"
                    alignItems="baseline"
                >
                    <H6Regular>Estimated bills</H6Regular>
                    <BodyLargeRegular>
                        {formatDollars(totalCents / 100, false)} total
                    </BodyLargeRegular>
                </XStack>

                <YStack width="100%">
                    {effectiveBills.map((bill) => (
                        <XStack
                            key={bill.category}
                            width="100%"
                            justifyContent="space-between"
                            alignItems="center"
                            paddingVertical={4}
                            {...(!isWidgetDisabled && {
                                onPress: () => {
                                    if (editingCategory !== bill.category) {
                                        setEditingCategory(bill.category);
                                    }
                                },
                                cursor: 'pointer',
                                ...(editingCategory === bill.category && {
                                    onMouseDown: (e: {
                                        preventDefault: () => void;
                                    }) => {
                                        e.preventDefault();
                                    },
                                }),
                            })}
                        >
                            <BodyMediumRegular color="$gray1100">
                                {bill.label}
                            </BodyMediumRegular>
                            {isNonMobileWeb ? (
                                <View
                                    paddingHorizontal={8}
                                    paddingVertical={4}
                                    borderRadius={999}
                                    backgroundColor="$gray200"
                                >
                                    <EditableAmountCell
                                        bill={bill}
                                        editedCents={bill.amountCents}
                                        isEditing={
                                            editingCategory === bill.category
                                        }
                                        isDisabled={isWidgetDisabled}
                                        onStartEdit={() => {
                                            setEditingCategory(bill.category);
                                        }}
                                        onCommit={(newCents) => {
                                            handleCommitAmount(
                                                bill.category,
                                                newCents,
                                            );
                                        }}
                                    />
                                </View>
                            ) : (
                                <View
                                    paddingHorizontal={8}
                                    paddingVertical={4}
                                    borderRadius={999}
                                    backgroundColor="$gray200"
                                >
                                    <BodyMediumRegular color="$gray1100">
                                        {formatDollars(
                                            bill.amountCents / 100,
                                            false,
                                        )}
                                    </BodyMediumRegular>
                                </View>
                            )}
                        </XStack>
                    ))}
                </YStack>

                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text="Looks good"
                    disabled={isWidgetDisabled}
                    width="full"
                    onPress={handleConfirm}
                />
            </YStack>
        </EditHintTooltip>
    );
};
